import { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useApi, useAlert } from "@/";
import {
  addLabelSettings,
  updateLabelPrintSettings,
} from "../../../app/slices";
import { CheckBoxInput, Button } from "@/components";
import { RightWrapper } from "../../../components";
import ShippingLabel from "../../../components/Invoice/ShippingLabel";

const SelectPrintMode = ({ value, onChange }) => {
  return (
    <div className="flex flex-col gap-3">
      <h1 className="text-headline4 font-nunito text-secondary/90 font-semibold">
        Print Layout
      </h1>
      <div className="flex gap-4">
        {/* 1x1 Layout Option */}
        <div
          onClick={() => onChange(1)}
          className={`cursor-pointer flex flex-col items-center border-2 rounded-md p-3 transition-all duration-200 ${
            value === 1
              ? "border-primary bg-primary/5"
              : "border-secondary/30 hover:border-secondary/50"
          }`}
        >
          {/* Visual representation of 1x1 layout */}
          <div className="w-24 h-32 border border-secondary/40 bg-white mb-2 flex items-center justify-center">
            <div className="w-20 h-28 bg-gray-100 border border-dashed border-secondary/60"></div>
          </div>
          <span
            className={`text-sm font-semibold font-nunito ${
              value === 1 ? "text-primary" : "text-secondary/50"
            }`}
          >
            Single Label
          </span>
          <span className="text-xs text-secondary/40 mt-1">
            1 label per page
          </span>
        </div>

        {/* 4x1 Layout Option */}
        <div
          onClick={() => onChange(4)}
          className={`cursor-pointer flex flex-col items-center border-2 rounded-md p-3 transition-all duration-200 ${
            value === 4
              ? "border-primary bg-primary/5"
              : "border-secondary/30 hover:border-secondary/50"
          }`}
        >
          {/* Visual representation of 4x1 layout */}
          <div className="w-24 h-32 border border-secondary/40 bg-white mb-2 grid grid-cols-2 gap-1 p-1">
            <div className="bg-gray-100 border border-dashed border-secondary/60"></div>
            <div className="bg-gray-100 border border-dashed border-secondary/60"></div>
            <div className="bg-gray-100 border border-dashed border-secondary/60"></div>
            <div className="bg-gray-100 border border-dashed border-secondary/60"></div>
          </div>
          <span
            className={`text-sm font-semibold font-nunito ${
              value === 4 ? "text-primary" : "text-secondary/50"
            }`}
          >
            Multiple Labels
          </span>
          <span className="text-xs text-secondary/40 mt-1">
            4 labels per page
          </span>
        </div>
      </div>
    </div>
  );
};

const LabelSetting = () => {
  const { alert, AlertDialog } = useAlert();
  const { callApi } = useApi();
  const dispatch = useDispatch();
  // Get settings from Redux
  const setting = useSelector(
    (state) => state.setting?.settings?.labelSettings?.config
  );
  const printSetting = useSelector(
    (state) => state.setting?.settings?.labelPrintSettings?.labelsPerPage
  );

  console.log(printSetting);

  // Initialize state based on Redux setting (ensure defaults exist)
  const [settings, setSettings] = useState({
    pickupAndReturnAddress: setting?.showPickupReturnAddress ?? false,
    productDetails: setting?.showProductDetails ?? false,
    productPriceCodAmount: setting?.showProductPrice ?? false,
    hideMobileNumber: setting?.hideMobileNumber ?? false,
    includeLogo: setting?.includeLogo ?? false,
  });
  const [printMode, setPrintMode] = useState(printSetting ?? 1);

  // Ensure settings update when Redux data is available
  useEffect(() => {
    if (setting) {
      setSettings({
        pickupAndReturnAddress: setting.showPickupReturnAddress,
        productDetails: setting.showProductDetails,
        productPriceCodAmount: setting.showProductPrice,
        hideMobileNumber: setting.hideMobileNumber,
        includeLogo: setting.includeLogo,
      });
    }
  }, [setting]);

  useEffect(() => {
    if (printSetting) {
      setPrintMode(printSetting);
    }
  }, [printSetting]);

  // Save settings API call
  const handleSave = useCallback(async () => {
    try {
      const payload = {
        config: {
          includeLogo: settings.includeLogo,
          showPickupReturnAddress: settings.pickupAndReturnAddress,
          showProductDetails: settings.productDetails,
          showProductPrice: settings.productPriceCodAmount,
          hideMobileNumber: settings.hideMobileNumber,
        },
      };
      const response = await callApi(
        "user/settings/updateLabelSettings",
        "PUT",
        payload
      );
      if (response.success) {
        alert("Settings updated successfully");
        dispatch(addLabelSettings(response.data.config));
      } else {
        alert("Failed to update settings");
      }
    } catch ({ error }) {
      console.log("Error updating settings:", error.message);
      alert("An error occurred while updating settings");
    }
  }, [settings, callApi, dispatch]);
  // Handle checkbox toggling dynamically
  const handleChange = useCallback((key) => {
    setSettings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);

  const handlePrintModeChange = useCallback(
    async (mode) => {
      console.log(mode);

      try {
        const response = await callApi(
          "/user/updateLabelPrintSettings",
          "GET",
          null,
          { perPage: mode }
        );

        if (response.success) {
          alert("Print settings updated successfully");
          dispatch(updateLabelPrintSettings(mode));
        } else {
          alert("Failed to update print settings");
        }

        setPrintMode(mode);
      } catch (error) {
        console.log(error);
      }
    },
    [printMode]
  );

  return (
    <RightWrapper>
      <div className="flex flex-col md:flex-row m-2 gap-6">
        {/* Left Section - Settings */}
        <div className="flex flex-col gap-6 md:gap-8 mt-3 w-full md:w-1/2">
          <div className="flex flex-col gap-1">
            <h1 className="md:text-headline3 text-headline4 font-nunito text-secondary/95 font-semibold">
              Label Setting
            </h1>
            <p className="text-body2 font-ptsans text-secondary/60 lg:w-4/5">
              Customize the label format to meet your specific needs, ensuring
              accurate and efficient shipping labels every time.
            </p>
          </div>

          {/* Checkbox Options */}
          <div className="flex flex-col gap-3">
            {[
              {
                label: "Pickup and Return Address",
                key: "pickupAndReturnAddress",
              },
              {
                label: "Product Details",
                key: "productDetails",
              },
              {
                label: "Product Price / COD Amount",
                key: "productPriceCodAmount",
              },
              {
                label: "Hide Mobile Number",
                key: "hideMobileNumber",
              },
              {
                label: "Include Logo",
                key: "includeLogo",
              },
            ].map(({ label, key }) => (
              <CheckBoxInput
                key={key}
                label={label}
                checked={Boolean(settings[key])} // Ensures always boolean
                onChange={() => handleChange(key)}
                labelClassName="text-caption md:text-body1"
              />
            ))}
          </div>

          <Button
            variant="default"
            size="sm"
            className="w-fit"
            onClick={handleSave}
          >
            Save Changes
          </Button>
          <SelectPrintMode value={printMode} onChange={handlePrintModeChange} />
        </div>

        {/* Right Section - Placeholder or Preview */}
        <div className="w-full md:w-1/2 flex flex-col mt-3 lg:items-center gap-1">
          <h1 className="md:text-headline3 text-headline4 font-nunito text-secondary/95 font-semibold mb-1">
            Label Preview
          </h1>
          <ShippingLabel
            config={{
              includeLogo: settings.includeLogo,
              showPickupReturnAddress: settings.pickupAndReturnAddress,
              showProductDetails: settings.productDetails,
              showProductPrice: settings.productPriceCodAmount,
              hideMobileNumber: settings.hideMobileNumber,
            }}
          />
        </div>
      </div>
      <AlertDialog />
    </RightWrapper>
  );
};

export default LabelSetting;
